import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { ICrewMember, ISelectedCrewDays, defaultTimezone } from "src/redux/reducers/CrewScheduleReducer";
import moment from "moment-timezone";
import CrewScheduleEventSummaryList, { ISummaryItem, ISummarySubHeader } from "../CrewScheduleEventSummaryList";
import { SearchAvailableCrewPairings } from "./SearchAvailableCrewPairings";
import { styled, Box, Accordion, AccordionSummary, AccordionDetails, Tooltip, Chip, Theme } from "@mui/material";
import { CrewMemberViolations } from "../CrewMemberViolations";
import { CrewMemberQualifications } from "../CrewMemberQualifications";
import { CrewBlockHours } from "./CrewBlockHours";
import { CrewViolationLastScan } from "./CrewViolationLastScan";
import { CrewSnapshot } from "./CrewSnapshot";
import { getClientSetting } from "src/redux/actions/App.Actions";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Selected Day items
import { ManageNFAs } from "src/pages/CrewSchedule/CrewScheduleHeader/AddEvents/ManageNFAs";
import { ManageLMs } from "src/pages/CrewSchedule/CrewScheduleHeader/AddEvents/ManageLMs";
import {
  IClientSettings,
  ClientSettingName,
  IQualificationOnCrew,
  IViolationResult,
} from "@vmosolutions/oc-interfaces";
import { PositionBadge } from "../PositionBadge";
import { IModalInfo } from "src/pages/CrewSchedule/CrewScheduleWindow";

interface ISelectedCrewDayContextProps {
  id: string;
  path: string[];
  selectedCrewDays: ISelectedCrewDays;
  setActiveEventModal?: (modalInfo: IModalInfo) => void;
}

const OCSelectedCrewDayContext = styled("div")(({ theme }) => ({
  "& .selected_day_manage_events": {
    margin: "15px 0px",
    padding: "10px",
    backgroundColor: theme.custom.timeline.contextListBackground,
    "& button.paint_btn": {
      padding: "2px 8px",
      fontSize: 12,
      fontWeight: 300,
    },
  },
}));

export const SelectedCrewDayContext = (props: ISelectedCrewDayContextProps): JSX.Element => {
  const { id, path, selectedCrewDays, setActiveEventModal } = props;
  const crewViolations = useSelector((state: IRootState) => state.crewSchedule.crewViolations);
  const loadedViolations = useSelector((state: IRootState) => state.crewSchedule.loadedViolations);
  const crew = useSelector((state: IRootState) => state.crewSchedule.crewMembers);
  const crewMember = useMemo(() => {
    return crew.find((value: ICrewMember) => value._id === selectedCrewDays?.crewId || value.isSelected);
  }, [crew, selectedCrewDays]);
  const [crewSnapshotSettings, setCrewSnapshotSettings] = useState<any>({});
  const isMultiDay = selectedCrewDays?.selectedTimes?.length > 1;
  const windowProperties = useSelector((state: IRootState) => state.crewSchedule.windowProperties[id]);
  const displayTimezone = windowProperties?.displayTimezone ?? defaultTimezone;
  const dayFormatted = useMemo(() => {
    if (!moment || !selectedCrewDays || selectedCrewDays.selectedTimes?.length === 0) {
      return "";
    }
    const firstDay = selectedCrewDays.selectedTimes[0];
    const lastDay = isMultiDay ? selectedCrewDays.selectedTimes[selectedCrewDays.selectedTimes.length - 1] : null;
    return isMultiDay
      ? `${moment.utc(firstDay).tz(displayTimezone.timezoneId).format("ddd, MMM D YYYY z")} - ${moment
          .utc(lastDay)
          .tz(displayTimezone.timezoneId)
          .format("ddd, MMM D YYYY z")}`
      : moment.utc(firstDay).tz(displayTimezone.timezoneId).format("ddd, MMM D YYYY z");
  }, [displayTimezone.timezoneId, selectedCrewDays, isMultiDay]);

  useEffect(() => {
    const getCrewSnapshotSetting = async (): Promise<void> => {
      const crewSnapshotSetting: IClientSettings = await getClientSetting(ClientSettingName.CrewSnapshot);
      setCrewSnapshotSettings(crewSnapshotSetting?.setValue);
    };
    getCrewSnapshotSetting();
  }, []);

  const summaryItems: Array<ISummaryItem | ISummarySubHeader> = useMemo(
    () =>
      crewMember
        ? [
            {
              key: `${crewMember._id}-email`,
              value: crewMember?.email,
              label: "Email:",
            },
            {
              key: `${crewMember._id}-phone#`,
              value: crewMember?.crewInfo?.phonePrimary ?? "Unspecified",
              label: "Primary Phone:",
            },
            {
              key: `${crewMember._id}-secondaryPhone#`,
              value:
                crewMember?.crewInfo?.phoneSecondary === undefined || crewMember?.crewInfo?.phoneSecondary === ""
                  ? "Unspecified"
                  : crewMember?.crewInfo?.phoneSecondary,
              label: "Secondary Phone:",
            },
            {
              key: `${crewMember._id}-positions`,
              value: (
                <Box sx={{ display: "inline-flex" }}>
                  {crewMember?.crewInfo?.positions.map((position, key) => {
                    return <PositionBadge key={key} position={position} filled={true} />;
                  })}
                </Box>
              ),
              label: `Position${crewMember?.crewInfo?.positions.length > 1 ? "s" : ""}:`,
            },
          ]
        : [],
    [crewMember]
  );

  const qualCounts: { error: number; warning: number; other: number } = useMemo(() => {
    const oneMonthAsMillis = moment.duration(1, "month").asMilliseconds();
    const oneWeekAsMillis = moment.duration(1, "week").asMilliseconds();
    const now = moment();
    const qualifications = crewMember?.crewInfo?.qualifications ?? [];

    const qualcount = { error: 0, warning: 0, other: 0 };
    qualifications.forEach((qual: IQualificationOnCrew) => {
      let goodQual = true;
      if (typeof qual.isQualified === "boolean" && qual.isQualified === false) {
        qualcount.error++;
        goodQual = false;
      }
      if (qual.expireDate) {
        const expireDate = new Date(qual.expireDate);
        if (moment(expireDate).diff(now) <= oneWeekAsMillis) {
          qualcount.error++;

          goodQual = false;
        } else if (moment(expireDate).diff(now) <= oneMonthAsMillis) {
          qualcount.warning++;
          goodQual = false;
        }
      }
      if (goodQual) {
        qualcount.other++;
      }
    });
    return qualcount;
  }, [crewMember?.crewInfo?.qualifications]);

  const [qualsOpen, setQualsOpen] = useState<boolean>(qualCounts.error + qualCounts.warning > 0 ? true : false);

  const crewMemberViolations = useMemo(
    () => crewViolations?.[selectedCrewDays?.crewId],
    [crewViolations, selectedCrewDays]
  );
  const crewMemberViolationsOnDay: any[] = useMemo(() => {
    const violations: any[] = [];
    const violationSet = new Set<string>();
    if (!crewMemberViolations || !selectedCrewDays || !loadedViolations) {
      return [];
    }
    selectedCrewDays.selectedTimes.forEach((selectedCrewDay: number) => {
      const day = moment.utc(selectedCrewDay);
      const vios = Object.values(crewMemberViolations?.violations)
        .flatMap(({ regulationResults }) => {
          return regulationResults;
        })
        .filter((vio: IViolationResult) => {
          if (!vio.period) {
            return false;
          }
          const periodStart = moment.utc(vio.period.start).startOf("day");
          const periodEnd = moment.utc(vio.period.end).endOf("day");
          const isBetween = day.isSameOrAfter(periodStart) && day.isSameOrBefore(periodEnd);
          return vio.status === "fail" && isBetween && !vio.noShow;
        });
      const violation: any[] = [];
      vios.forEach((vio: IViolationResult) => {
        if (violationSet.has(vio._id)) {
          return;
        }

        violationSet.add(vio._id);
        violation.push({
          id: vio._id,
          title: vio.title,
          regulation: vio.regulation,
          cause: vio.cause,
          start: vio.period.start,
          end: vio.period.end,
        });
      });
      violations.push(...violation);
    });

    return violations;
  }, [crewMemberViolations, selectedCrewDays, loadedViolations]);

  const [violationsOpen, setViolationsOpen] = useState<boolean>(crewMemberViolationsOnDay.length > 0 ? true : false);

  useEffect(() => {
    setQualsOpen(qualCounts.error + qualCounts.warning > 0 ? true : false);
    setViolationsOpen(crewMemberViolationsOnDay.length > 0 ? true : false);
  }, [qualCounts, crewMemberViolationsOnDay]);

  const chipStyles = (theme: Theme): any => ({
    borderRadius: "2px",
    height: "18px",
    my: 0,
    mr: "2px",
    "& .MuiChip-label": {
      px: "6px",
      fontWeight: 900,
      py: "0px",
      fontSize: "0.7em",
      color: theme.palette.text.primary,
      my: 0,
      mx: "3px",
    },
  });
  // If there is no crewMember associated with selected day or tshere is more than one day selected, show nothing.
  return !crewMember ? (
    <></>
  ) : (
    <OCSelectedCrewDayContext>
      {selectedCrewDays?.selectedTimes?.length > 0 ? (
        <>
          <CrewScheduleEventSummaryList
            hideTitle={true}
            key={`${props.id}-${crewMember._id}-summaryList`}
            items={summaryItems}
          />

          <Accordion expanded={violationsOpen} defaultExpanded={violationsOpen}>
            <AccordionSummary
              sx={{ "& .MuiAccordionSummary-content": { display: "flex", justifyContent: "space-between" } }}
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${props.id}-${crewMember._id}-qualsList-panelContent`}
              id={`${props.id}-${crewMember._id}-qualsList-panelHeader`}
              onClick={() => setViolationsOpen(!violationsOpen)}
            >
              <Box>Violations</Box>
              <Box>
                {crewMemberViolationsOnDay.length > 0 && (
                  <Chip
                    size="small"
                    sx={(theme) => chipStyles(theme)}
                    color={"error"}
                    label={crewMemberViolationsOnDay.length}
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <CrewMemberViolations selectedCrewDays={selectedCrewDays} />
            </AccordionDetails>
          </Accordion>
          <Accordion expanded={qualsOpen} defaultExpanded={qualsOpen}>
            <AccordionSummary
              sx={{ "& .MuiAccordionSummary-content": { display: "flex", justifyContent: "space-between" } }}
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${props.id}-${crewMember._id}-qualsList-panelContent`}
              id={`${props.id}-${crewMember._id}-qualsList-panelHeader`}
              onClick={() => setQualsOpen(!qualsOpen)}
            >
              <Box>Qualifications</Box>
              <Box>
                {qualCounts.error > 0 && (
                  <Chip size="small" sx={(theme) => chipStyles(theme)} color={"error"} label={qualCounts.error} />
                )}
                {qualCounts.warning > 0 && (
                  <Chip size="small" sx={(theme) => chipStyles(theme)} color={"warning"} label={qualCounts.warning} />
                )}
                {qualCounts.other > 0 && (
                  <Chip
                    size="small"
                    sx={(theme) => ({ ...chipStyles(theme), backgroundColor: theme.palette.grey[700] })}
                    color={"info"}
                    label={qualCounts.other}
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <CrewMemberQualifications qualifications={crewMember?.crewInfo?.qualifications} />
            </AccordionDetails>
          </Accordion>
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${props.id}-${crewMember._id}-nfa-panelContent`}
              id={`${props.id}-${crewMember._id}-nfa-panelHeader`}
            >
              <Tooltip title="Non Flying Assignments">
                <Box>NFA's</Box>
              </Tooltip>
            </AccordionSummary>
            <AccordionDetails>
              <ManageNFAs id={id} path={path} />
            </AccordionDetails>
          </Accordion>
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${props.id}-${crewMember._id}-lm-panelContent`}
              id={`${props.id}-${crewMember._id}-lm-panelHeader`}
            >
              Crew Logic Modifiers
            </AccordionSummary>
            <AccordionDetails>
              <ManageLMs manageSingleDay={!isMultiDay} contextMenuForm={true} />
            </AccordionDetails>
          </Accordion>
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${props.id}-${crewMember._id}-violations-panelContent`}
              id={`${props.id}-${crewMember._id}-violations-panelHeader`}
            >
              Pairings
            </AccordionSummary>
            <AccordionDetails>
              <SearchAvailableCrewPairings
                id={id}
                path={path}
                crewMember={crewMember}
                selectedTimes={selectedCrewDays.selectedTimes}
                setActiveEventModal={setActiveEventModal}
              />
            </AccordionDetails>
          </Accordion>
        </>
      ) : (
        <>
          <CrewScheduleEventSummaryList
            key={`${props.id}-${crewMember._id}-summaryList`}
            items={summaryItems}
            hideTitle={true}
          />
          <CrewViolationLastScan id={id} path={path} crewId={crewMember?._id} />
          <CrewBlockHours id={id} path={path} crewId={crewMember?._id} />
          {crewSnapshotSettings?.enabled && <CrewSnapshot crewId={crewMember?._id} />}
        </>
      )}
    </OCSelectedCrewDayContext>
  );
};
